import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import QuestionCard from '../components/QuestionCard';
import useQuestionStore from '../stores/questionStore';
import useProgressStore from '../stores/progressStore';
import { getEra, isLiteratureQuestion, ERA_OPTIONS } from '../utils/questionMeta';

// ── Filter Pills Component ───────────────────────────────────────────────
function FilterPills({
  categories, selectedCats, setSelectedCats,
  eraFilter, setEraFilter,
  statusFilter, setStatusFilter,
  literatureOnly, setLiteratureOnly,
  poolCount,
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card p-3 space-y-2">
      {/* Top row: always visible — era pills + toggle */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {[
            { value: 'all', label: 'All' },
            { value: 'unanswered', label: 'Unseen' },
            { value: 'missed', label: 'Missed' },
            { value: 'unsure', label: "Don't Know" },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                statusFilter === opt.value
                  ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40'
                  : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-xs text-slate-500 hover:text-slate-300 shrink-0 px-2 py-1"
        >
          {expanded ? '▲ Less' : '▼ More'}
        </button>
      </div>

      {/* Expanded: era, literature, categories */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden space-y-2"
          >
            {/* Era + Literature */}
            <div className="flex flex-wrap gap-1.5">
              {ERA_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setEraFilter(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    eraFilter === opt.value
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40'
                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
                >
                  {opt.emoji ? `${opt.emoji} ` : ''}{opt.label}
                </button>
              ))}
              <button
                onClick={() => setLiteratureOnly(v => !v)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  literatureOnly
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                    : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                }`}
              >
                📚 Literature
              </button>
            </div>

            {/* Categories */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-500 font-medium">Categories</span>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedCats([...categories])} className="text-xs text-teal-400 hover:text-teal-300">All</button>
                  <span className="text-slate-600">·</span>
                  <button onClick={() => setSelectedCats([])} className="text-xs text-slate-500 hover:text-slate-300">None</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCats(prev =>
                      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
                    )}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      selectedCats.includes(cat)
                        ? 'bg-teal-500/20 text-teal-400 border-teal-500/40'
                        : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-xs text-slate-500">{poolCount} questions match</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Confidence Buttons ───────────────────────────────────────────────────
function ConfidenceButtons({ onSelect }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-3 gap-2"
    >
      <button
        onClick={() => onSelect('know')}
        className="h-12 rounded-xl bg-emerald-500/15 border-2 border-emerald-500/40 text-emerald-400 font-semibold text-sm hover:bg-emerald-500/25 active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
      >
        ✅ Know it
      </button>
      <button
        onClick={() => onSelect('kinda')}
        className="h-12 rounded-xl bg-amber-500/15 border-2 border-amber-500/40 text-amber-400 font-semibold text-sm hover:bg-amber-500/25 active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
      >
        🤔 Kinda
      </button>
      <button
        onClick={() => onSelect('unsure')}
        className="h-12 rounded-xl bg-red-500/15 border-2 border-red-500/40 text-red-400 font-semibold text-sm hover:bg-red-500/25 active:scale-[0.97] transition-all flex items-center justify-center gap-1.5"
      >
        ❌ Don't Know
      </button>
    </motion.div>
  );
}

// ── Category breakdown (results) ─────────────────────────────────────────
function CategoryBreakdown({ answers }) {
  const grouped = useMemo(() => {
    const map = {};
    answers.forEach(({ question, isCorrect }) => {
      const cat = question.category;
      if (!map[cat]) map[cat] = { correct: 0, total: 0 };
      map[cat].total += 1;
      if (isCorrect) map[cat].correct += 1;
    });
    return Object.entries(map)
      .map(([cat, v]) => ({ cat, ...v, pct: Math.round((v.correct / v.total) * 100) }))
      .sort((a, b) => a.pct - b.pct);
  }, [answers]);

  if (grouped.length === 0) return null;

  return (
    <div className="space-y-2">
      {grouped.map(({ cat, correct, total, pct }) => (
        <div key={cat}>
          <div className="flex items-center justify-between text-xs mb-0.5">
            <span className="text-slate-300 truncate flex-1">{cat}</span>
            <span className={`ml-2 font-semibold ${pct >= 75 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {correct}/{total} ({pct}%)
            </span>
          </div>
          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className={`h-full rounded-full ${pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Shuffle helper ───────────────────────────────────────────────────────
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Main Practice Component ──────────────────────────────────────────────
export default function Practice() {
  const questions = useQuestionStore((s) => s.questions);
  const categories = useQuestionStore((s) => s.getCategories());
  const userAnswers = useQuestionStore((s) => s.userAnswers);
  const confidenceRatings = useQuestionStore((s) => s.confidenceRatings);
  const answerQuestion = useQuestionStore((s) => s.answerQuestion);
  const setConfidence = useQuestionStore((s) => s.setConfidence);
  const toggleFlag = useQuestionStore((s) => s.toggleFlag);
  const flaggedQuestions = useQuestionStore((s) => s.flaggedQuestions);
  const recordAnswer = useProgressStore((s) => s.recordAnswer);
  const savePracticeResult = useProgressStore((s) => s.savePracticeResult);

  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter'); // 'weak' = unsure + don't know
  const modeParam = searchParams.get('mode'); // legacy: 'weak' | 'missed'

  const [selectedCats, setSelectedCats] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [eraFilter, setEraFilter] = useState('');
  const [literatureOnly, setLiteratureOnly] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [sessionAnswers, setSessionAnswers] = useState([]);
  const [finished, setFinished] = useState(false);
  const [initialized, setInitialized] = useState(false);
  // filterKey tracks explicit filter changes — avoids reshuffle when answers/confidence mutate
  const [filterKey, setFilterKey] = useState(0);

  // Wrap filter setters to bump filterKey
  const updateSelectedCats = useCallback((v) => { setSelectedCats(v); setFilterKey(k => k + 1); }, []);
  const updateStatusFilter = useCallback((v) => { setStatusFilter(v); setFilterKey(k => k + 1); }, []);
  const updateEraFilter = useCallback((v) => { setEraFilter(v); setFilterKey(k => k + 1); }, []);
  const updateLiteratureOnly = useCallback((v) => { setLiteratureOnly(v); setFilterKey(k => k + 1); }, []);

  // Build filtered pool for display count (uses live data)
  const filteredPool = useMemo(() => {
    let pool = selectedCats.length > 0
      ? questions.filter(q => selectedCats.includes(q.category))
      : [...questions];

    if (statusFilter === 'unanswered') {
      pool = pool.filter(q => !userAnswers[q.id]);
    } else if (statusFilter === 'missed') {
      pool = pool.filter(q => userAnswers[q.id] && !userAnswers[q.id].isCorrect);
    } else if (statusFilter === 'unsure') {
      pool = pool.filter(q => {
        const c = confidenceRatings[q.id];
        return c === 'unsure' || c === 'kinda';
      });
    }

    if (eraFilter) {
      pool = pool.filter(q => getEra(q) === eraFilter);
    }
    if (literatureOnly) {
      pool = pool.filter(q => isLiteratureQuestion(q));
    }

    return pool;
  }, [questions, selectedCats, statusFilter, eraFilter, literatureOnly, userAnswers, confidenceRatings]);

  // Auto-configure from URL params on first load
  useEffect(() => {
    if (!questions.length) return;
    if (initialized) return;

    if (filterParam === 'weak') {
      setStatusFilter('unsure');
    } else if (modeParam === 'weak') {
      const stats = useQuestionStore.getState().getStats();
      const weakCats = Object.entries(stats.byCategory)
        .filter(([, v]) => v.answered >= 3 && v.mastery < 75)
        .map(([cat]) => cat);
      if (weakCats.length) setSelectedCats(weakCats);
    } else if (modeParam === 'missed') {
      setStatusFilter('missed');
    }
    setFilterKey(1);
    setInitialized(true);
  }, [questions.length, filterParam, modeParam, initialized]);

  // Build/rebuild session ONLY on explicit filter changes (filterKey) — not on answer/confidence changes
  useEffect(() => {
    if (!initialized || !questions.length) return;
    // Compute pool from current filter state using store snapshot to avoid stale closure
    const state = useQuestionStore.getState();
    let pool = selectedCats.length > 0
      ? questions.filter(q => selectedCats.includes(q.category))
      : [...questions];

    if (statusFilter === 'unanswered') {
      pool = pool.filter(q => !state.userAnswers[q.id]);
    } else if (statusFilter === 'missed') {
      pool = pool.filter(q => state.userAnswers[q.id] && !state.userAnswers[q.id].isCorrect);
    } else if (statusFilter === 'unsure') {
      pool = pool.filter(q => {
        const c = state.confidenceRatings[q.id];
        return c === 'unsure' || c === 'kinda';
      });
    }
    if (eraFilter) {
      pool = pool.filter(q => getEra(q) === eraFilter);
    }
    if (literatureOnly) {
      pool = pool.filter(q => isLiteratureQuestion(q));
    }

    setSessionQuestions(shuffle(pool));
    setSessionAnswers([]);
    setCurrentIndex(0);
    setFinished(false);
  }, [filterKey, initialized, questions]);

  const handleAnswer = (selectedIndex) => {
    const q = sessionQuestions[currentIndex];
    const isCorrect = answerQuestion(q.id, selectedIndex);
    // Note: do NOT call recordAnswer here — savePracticeResult handles it to avoid double-counting
    setSessionAnswers(prev => [...prev, { questionId: q.id, selectedIndex, isCorrect, question: q }]);
  };

  const handleConfidence = useCallback((rating) => {
    const q = sessionQuestions[currentIndex];
    setConfidence(q.id, rating);

    // Advance to next question
    if (currentIndex < sessionQuestions.length - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      // Session complete
      const finalAnswers = sessionAnswers;
      const score = finalAnswers.filter(a => a.isCorrect).length;
      if (finalAnswers.length > 0) {
        savePracticeResult({ score, total: finalAnswers.length, answers: finalAnswers });
      }
      setFinished(true);
    }
  }, [currentIndex, sessionQuestions, sessionAnswers, setConfidence, savePracticeResult]);

  const endSession = () => {
    if (sessionAnswers.length > 0) {
      const score = sessionAnswers.filter(a => a.isCorrect).length;
      savePracticeResult({ score, total: sessionAnswers.length, answers: sessionAnswers });
    }
    setFinished(true);
  };

  const reset = () => {
    const shuffled = shuffle(filteredPool);
    setSessionQuestions(shuffled);
    setSessionAnswers([]);
    setCurrentIndex(0);
    setFinished(false);
  };

  const retryMissed = () => {
    const missed = sessionAnswers.filter(a => !a.isCorrect).map(a => a.question);
    if (!missed.length) return;
    setSessionQuestions(shuffle(missed));
    setSessionAnswers([]);
    setCurrentIndex(0);
    setFinished(false);
  };

  // ── LOADING STATE ────────────────────────────────────────────────────
  if (!questions.length) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        <div className="card p-8 text-center">
          <p className="text-slate-400">Loading questions...</p>
        </div>
      </div>
    );
  }

  // ── RESULTS SCREEN ───────────────────────────────────────────────────
  if (finished) {
    const correct = sessionAnswers.filter(a => a.isCorrect).length;
    const total = sessionAnswers.length;
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    const missedInSession = sessionAnswers.filter(a => !a.isCorrect);

    const ringColor = pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
    const r = 48;
    const circ = 2 * Math.PI * r;
    const dash = circ * (1 - pct / 100);
    const msg = pct >= 85 ? 'Excellent work! 🎉' : pct >= 75 ? 'Above passing threshold ✓' : pct >= 50 ? 'Getting there — keep drilling' : total > 0 ? 'Needs work — review the explanations' : 'No questions answered';

    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="card p-8 text-center">
          {total > 0 && (
            <div className="flex justify-center mb-4">
              <svg width={120} height={120} viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={r} fill="none" stroke="#1e293b" strokeWidth="10" />
                <motion.circle
                  cx="60" cy="60" r={r}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={circ}
                  initial={{ strokeDashoffset: circ }}
                  animate={{ strokeDashoffset: dash }}
                  transition={{ duration: 1.2, ease: 'easeOut' }}
                  transform="rotate(-90 60 60)"
                />
                <text x="60" y="57" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold" fontFamily="system-ui">{pct}%</text>
                <text x="60" y="72" textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="system-ui">{correct}/{total}</text>
              </svg>
            </div>
          )}
          <h2 className="text-2xl font-bold text-white">Session Complete</h2>
          <p className={`text-sm mt-1.5 font-medium ${pct >= 75 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{msg}</p>
          <div className="flex gap-3 mt-6 justify-center flex-wrap">
            <button onClick={reset} className="btn-primary">New Session</button>
            {missedInSession.length > 0 && (
              <button
                onClick={retryMissed}
                className="px-4 py-2 bg-rose-500/20 border border-rose-500/40 text-rose-400 rounded-lg text-sm font-semibold hover:bg-rose-500/30 transition-colors"
              >
                🔁 Retry {missedInSession.length} Missed
              </button>
            )}
            <Link to="/" className="btn-secondary">Dashboard</Link>
          </div>
        </motion.div>

        {total > 0 && (
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Category Breakdown</h3>
            <CategoryBreakdown answers={sessionAnswers} />
          </div>
        )}
      </div>
    );
  }

  // ── EMPTY POOL ───────────────────────────────────────────────────────
  if (sessionQuestions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-4">
        <FilterPills
          categories={categories}
          selectedCats={selectedCats}
          setSelectedCats={updateSelectedCats}
          eraFilter={eraFilter}
          setEraFilter={updateEraFilter}
          statusFilter={statusFilter}
          setStatusFilter={updateStatusFilter}
          literatureOnly={literatureOnly}
          setLiteratureOnly={updateLiteratureOnly}
          poolCount={filteredPool.length}
        />
        <div className="card p-8 text-center">
          <p className="text-slate-400 text-lg mb-2">No questions match your filters</p>
          <p className="text-slate-500 text-sm">Try adjusting your filters above</p>
          <button
            onClick={() => { updateSelectedCats([]); updateStatusFilter('all'); updateEraFilter(''); updateLiteratureOnly(false); }}
            className="btn-primary mt-4"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    );
  }

  // ── SESSION SCREEN (question-first) ──────────────────────────────────
  const currentQ = sessionQuestions[currentIndex];
  const currentAnswer = sessionAnswers.find(a => a.questionId === currentQ?.id);
  const hasAnswered = !!currentAnswer;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-4">
      {/* Filter pills — always at top */}
      <FilterPills
        categories={categories}
        selectedCats={selectedCats}
        setSelectedCats={updateSelectedCats}
        eraFilter={eraFilter}
        setEraFilter={updateEraFilter}
        statusFilter={statusFilter}
        setStatusFilter={updateStatusFilter}
        literatureOnly={literatureOnly}
        setLiteratureOnly={updateLiteratureOnly}
        poolCount={filteredPool.length}
      />

      {/* Progress bar + End button */}
      <div className="flex items-center gap-3">
        <button onClick={endSession} className="text-slate-400 hover:text-white text-sm shrink-0">✕ End</button>
        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-teal-500 rounded-full"
            animate={{ width: `${((currentIndex + 1) / sessionQuestions.length) * 100}%` }}
          />
        </div>
        <span className="text-sm text-slate-400 font-mono shrink-0">{currentIndex + 1}/{sessionQuestions.length}</span>
      </div>

      {/* Question Card */}
      {currentQ && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQ.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <QuestionCard
              question={currentQ}
              showAnswer={true}
              onAnswer={handleAnswer}
              onFlag={() => toggleFlag(currentQ.id)}
              isFlagged={flaggedQuestions.has(currentQ.id)}
              selectedAnswer={currentAnswer?.selectedIndex ?? null}
              readOnly={hasAnswered}
            />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Confidence buttons — appear after answering, replace old "Next" button */}
      {hasAnswered && (
        <ConfidenceButtons onSelect={handleConfidence} />
      )}
    </div>
  );
}
